import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  styled,
  Typography,
  useTheme,
} from "@schaeffler-commerce/design-system";
import CloseIcon from "@mui/icons-material/Close";
import type { AsmSessionSnapshot, AutoSuggestionCustomer, PersonifyRequest } from "~/features/assistedService/types";
import { resolveAsmMessage } from "~/features/assistedService/utils/asmMessage";
import { AsmEmulateForm } from "../AsmEmulateForm/AsmEmulateForm";
import { AsmLoginForm } from "../AsmLoginForm/AsmLoginForm";
import { AsmTimer } from "../AsmTimer/AsmTimer";
import { Container } from "~/common/components/Layout";
import { C_INFORMATION_CONTAINER, C_ON_INFORMATION_CONTAINER, C_WHITE } from "@schaeffler-commerce/design-system";
import { ChevronDownIcon, ChevronUpIcon } from "@storybook/icons";

interface AlertMessage {
  messageCode: string;
  alertClass: string | null;
}

export interface AssistedServiceBarViewProps {
  state: AsmSessionSnapshot;
  remainingSeconds: number;
  collapsed: boolean;
  alert: AlertMessage | null;
  loginPending: boolean;
  personifyPending: boolean;
  onLogin: () => void;
  onPersonify: (request: PersonifyRequest) => void;
  onPersonifyStop: () => void;
  onLogout: () => void;
  onClose: () => void;
  onResetTimer: () => void;
  onToggleCollapse: () => void;
  onSearchCustomers: (customerId: string, signal?: AbortSignal) => Promise<AutoSuggestionCustomer[]>;
}

/**
 * Pure, prop-driven presentation of the ASM bar across the three states
 * (login / logged-in-not-emulating / emulating). Storybook renders this; the connected
 * {@link AssistedServiceBar} wires the runtime side effects via `useAssistedService`.
 */
export const AssistedServiceBarView = ({
  state,
  remainingSeconds,
  collapsed,
  alert,
  loginPending,
  personifyPending,
  onLogin,
  onPersonify,
  onPersonifyStop,
  onLogout,
  onClose,
  onResetTimer,
  onToggleCollapse,
  onSearchCustomers,
}: AssistedServiceBarViewProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const agent = state.agent;
  const emulated = state.emulatedUser;

  // Route the (single) alert to the form it pertains to: credential/login errors while signing in,
  // emulate errors while picking a customer, and a top-level alert only when no form is visible
  // (emulating state). This restores per-form error context and field highlighting (alertClass).
  const loginAlert = !agent ? alert : null;
  const emulateAlert = agent && !emulated ? alert : null;
  const globalAlert = agent && emulated ? alert : null;

  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <FullbleedContainer>
        <Paper
          role="region"
          aria-label={t("asm.login.title")}
          elevation={0}
          sx={{
            background: C_INFORMATION_CONTAINER,
            color: "infoContainer.contrastText",
            position: "relative",
            borderRadius: 0,
            px: 2,
            py: 1,
            ...(collapsed ? { py: 0, maxHeight: theme.spacing(1.25), overflow: "hidden" } : {}),
          }}
        >
          <IconButton
            aria-label={t("asm.emulate.close")}
            onClick={onClose}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {globalAlert && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {resolveAsmMessage(t, globalAlert.messageCode)}
            </Alert>
          )}

          {!agent && <AsmLoginForm pending={loginPending} alert={loginAlert} onLogin={onLogin} />}

          {agent && (
            <Stack spacing={1}>
              <Stack
                direction={{ phone: "column", tablet: "row" }}
                spacing={2}
                sx={{
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="bodyMedium">
                  {t("asm.login.logged")} {agent.name}
                </Typography>
                <AsmTimer remainingSeconds={remainingSeconds} onReset={onResetTimer} />
                <Button variant="text" size="small" onClick={onLogout}>
                  {t("asm.logout")}
                </Button>
              </Stack>

              {emulated ? (
                <Stack
                  direction={{ phone: "column", tablet: "row" }}
                  spacing={2}
                  sx={{
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="bodyMedium">
                    {emulated.name} ({emulated.uid})
                  </Typography>
                  <Button variant="text" size="small" onClick={onPersonifyStop}>
                    {t("asm.emulate.end")}
                  </Button>
                </Stack>
              ) : (
                <AsmEmulateForm
                  pending={personifyPending}
                  alert={emulateAlert}
                  onPersonify={onPersonify}
                  onSearchCustomers={onSearchCustomers}
                />
              )}
            </Stack>
          )}
          <CollapseButton aria-label={t("asm.emulate.collapse")} onClick={onToggleCollapse} size={"large"}>
            {collapsed ? <ChevronDownIcon fontSize={"large"} /> : <ChevronUpIcon fontSize={"large"} />}
          </CollapseButton>
        </Paper>
      </FullbleedContainer>
    </Box>
  );
};

const FullbleedContainer = styled(Container)({
  width: "100vw",
  position: "relative",
  flex: "none",
  marginLeft: "calc(var(--gutter) * -1)",
  marginRight: "calc(var(--gutter) * -1)",
  background: C_WHITE,
});

const CollapseButton = styled(IconButton)({
  borderRadius: 0,
  background: C_INFORMATION_CONTAINER,
  color: C_ON_INFORMATION_CONTAINER,
  zIndex: 1000000,
  position: "absolute",
  left: 0,
  bottom: 0,
  transform: "translateY(100%)",
});
